/**
 * Wave E2 — shared Vision food-check logic.
 *
 * Used by:
 *   - scripts/audit-dish-images.ts (backfill audit, runs in Node)
 *   - src/lib/imageCache.ts (write-time gate after image gen, runs in
 *     Capacitor WebView) — wire-up is a follow-up PR.
 *
 * Single source of truth for the Vision prompt + schema parsing so the audit
 * and the live cache can't drift. No DOM or Node-only imports — uses the
 * `fetch` global (available in both Node 22+ and Capacitor WebView).
 */

export interface VisionVerdict {
  is_food: boolean;
  matches_dish: boolean;
  quality: "good" | "meh" | "bad";
  reason: string;
}

export type VisionResult = VisionVerdict | { error: string };

export const VISION_PROMPT = `You are a strict food image classifier for a restaurant menu app.

Given a dish name and an image, judge:
1. is_food: Does the image's PRIMARY SUBJECT depict food or a beverage (drink, cocktail, beer, wine, coffee, juice, water)? Background props are fine — the subject must be food/drink.
2. matches_dish: Is the image plausibly a depiction of the named dish? The name may be in canonical-key form (lowercase, hyphenated, words may be alphabetized) — interpret loosely. A close-enough match counts (e.g. a burger image for "cheese-burger" name = match). A clearly unrelated dish does not (e.g. salad for "burger" = no match).
3. quality: One of "good" (sharp, appetizing, well-plated, professional-looking), "meh" (acceptable but uninspiring), or "bad" (deformed, unrealistic, blurry, weird artifacts, off-color, or unappealing).
4. reason: Short explanation, max 12 words.

Respond with ONLY a single JSON object on one line. No prose, no markdown. Schema:
{"is_food": boolean, "matches_dish": boolean, "quality": "good"|"meh"|"bad", "reason": string}`;

const GEMINI_MODEL = "gemini-2.5-flash-lite";
const GEMINI_URL = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;

/** De-canonicalize a dish name like "thai-lime-prawn-soup" → "thai lime prawn soup". */
export function readableDishName(canonical: string): string {
  return canonical.replace(/-/g, " ").trim();
}

/**
 * Tolerant JSON extraction — Gemini occasionally wraps responses in markdown
 * code fences or appends trailing whitespace/prose even with
 * responseMimeType: "application/json".
 */
export function extractJson(text: string): unknown | null {
  try {
    return JSON.parse(text);
  } catch {}
  const stripped = text
    .replace(/^\s*```(?:json)?\s*/i, "")
    .replace(/\s*```\s*$/i, "")
    .trim();
  try {
    return JSON.parse(stripped);
  } catch {}
  const firstBrace = text.indexOf("{");
  const lastBrace = text.lastIndexOf("}");
  if (firstBrace === -1 || lastBrace <= firstBrace) return null;
  try {
    return JSON.parse(text.slice(firstBrace, lastBrace + 1));
  } catch {
    return null;
  }
}

/**
 * Validate a parsed verdict against the schema. Returns the typed verdict or
 * null if the shape doesn't match.
 */
export function validateVerdict(raw: unknown): VisionVerdict | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (
    typeof r.is_food !== "boolean" ||
    typeof r.matches_dish !== "boolean" ||
    !["good", "meh", "bad"].includes(r.quality as string) ||
    typeof r.reason !== "string"
  ) {
    return null;
  }
  return r as unknown as VisionVerdict;
}

/**
 * Distinguish transient failures (rate limits, network, parse hiccups —
 * retry-able later) from definitive data-integrity failures (corrupt /
 * truncated images, malformed data URLs, Gemini rejecting the image as
 * unprocessable — these rows are bad and should be flagged so users stop
 * seeing them).
 */
export function isDataIntegrityError(errorMsg: string): boolean {
  return (
    errorMsg.startsWith("corrupt:") ||
    errorMsg.startsWith("image_url is not a data URL") ||
    errorMsg.includes("Unable to process input image")
  );
}

export interface CheckDishImageOptions {
  /** Gemini API key. Required. */
  apiKey: string;
  /** Minimum acceptable base64 payload size in bytes (default 1000). Anything below is auto-flagged as corrupt. */
  minBase64Bytes?: number;
  /** Optional AbortSignal so callers can cancel long calls. */
  signal?: AbortSignal;
  /**
   * Number of retry attempts on transient failures (HTTP 5xx / 429 / network).
   * Default 0 — meant for the live write-time gate where latency matters.
   * The audit script passes 3 to mop up the 503/429 stragglers that left
   * rows in unreviewed limbo on the original Wave E1 pass.
   */
  retries?: number;
  /** Base backoff in ms (default 800ms). Doubles each attempt + adds jitter. */
  retryBackoffMs?: number;
}

/** True if the error message looks like a transient failure worth retrying. */
function isRetryableError(errorMsg: string): boolean {
  // HTTP 5xx (server / Gemini availability) and 429 (rate limit) are retryable.
  // Network-ish hiccups (fetch failed / network / abort that wasn't intentional)
  // are also worth a quick retry — the alternative is leaving a row unreviewed.
  return (
    /^HTTP 5\d\d/.test(errorMsg) ||
    /^HTTP 429/.test(errorMsg) ||
    /fetch failed/i.test(errorMsg) ||
    /network/i.test(errorMsg) ||
    /ECONNRESET|ETIMEDOUT|EAI_AGAIN/.test(errorMsg)
  );
}

/**
 * Classify a dish image via Gemini Vision.
 *
 * The image is passed as a data URL (e.g. "data:image/jpeg;base64,…"). On
 * success, returns a `VisionVerdict`. On failure, returns `{ error: string }`
 * — check `isDataIntegrityError(error)` to decide whether to auto-flag the
 * underlying row.
 */
export async function checkDishImage(
  dishName: string,
  description: string | null | undefined,
  imageDataUrl: string,
  opts: CheckDishImageOptions,
): Promise<VisionResult> {
  const minBytes = opts.minBase64Bytes ?? 1000;

  const match = imageDataUrl.match(/^data:(image\/[a-z]+);base64,(.+)$/i);
  if (!match) return { error: "image_url is not a data URL" };
  const mimeType = match[1];
  const base64 = match[2];

  // Pre-flight: corrupt-row check. Skip the Vision call entirely on rows that
  // are clearly truncated; Gemini will reject them with HTTP 400 anyway.
  if (base64.length < minBytes) {
    return { error: `corrupt: base64 payload only ${base64.length} bytes` };
  }

  const userPrompt =
    `Dish name: "${readableDishName(dishName)}"` +
    (description ? `\nDescription: "${description}"` : "") +
    `\nJudge the image.`;

  const body = {
    contents: [
      {
        role: "user",
        parts: [
          { text: VISION_PROMPT + "\n\n" + userPrompt },
          { inlineData: { mimeType, data: base64 } },
        ],
      },
    ],
    generationConfig: { temperature: 0, responseMimeType: "application/json" },
  };

  const maxAttempts = 1 + Math.max(0, opts.retries ?? 0);
  const baseBackoff = opts.retryBackoffMs ?? 800;
  let lastError = "";
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const r = await fetch(`${GEMINI_URL}?key=${opts.apiKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: opts.signal,
      });
      if (!r.ok) {
        const text = await r.text();
        lastError = `HTTP ${r.status}: ${text.slice(0, 200)}`;
        if (attempt < maxAttempts - 1 && isRetryableError(lastError)) {
          // Exponential backoff with jitter — keeps retry storms from
          // synchronizing across the concurrent worker pool.
          const delay = baseBackoff * Math.pow(2, attempt) + Math.random() * 400;
          await new Promise((res) => setTimeout(res, delay));
          continue;
        }
        return { error: lastError };
      }
      const data = (await r.json()) as {
        candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
      };
      const text = data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!text) return { error: "no text in Gemini response" };
      const parsed = extractJson(text);
      if (!parsed) return { error: "could not parse JSON: " + text.slice(0, 200) };
      const verdict = validateVerdict(parsed);
      if (!verdict) return { error: "schema mismatch: " + text.slice(0, 200) };
      return verdict;
    } catch (e) {
      lastError = e instanceof Error ? e.message : String(e);
      if (attempt < maxAttempts - 1 && isRetryableError(lastError)) {
        const delay = baseBackoff * Math.pow(2, attempt) + Math.random() * 400;
        await new Promise((res) => setTimeout(res, delay));
        continue;
      }
      return { error: lastError };
    }
  }
  return { error: lastError || "exhausted retries" };
}

/** Type guard. */
export function isVerdict(r: VisionResult): r is VisionVerdict {
  return "is_food" in r;
}
